import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import countries from "./countries.json";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const chevronRef = useRef(null);
  const [flipped, setFlipped] = useState(false);

  const menuItems = [
    { key: "home", label: "About Us", path: "/About" },
    { key: "services", label: "Services", path: "Services" },
    { key: "reports", label: "Projects", path: "/ProjectPage" },
    { key: "resources", label: "Reports", path: "/Report" },
    { key: "gallery", label: "Gallery", path: "/Gallery" },
  ];

  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === "/" || currentPath === "/About") {
      setActiveTab("home");
    } else {
      const foundItem = menuItems.find((item) => item.path === currentPath);
      setActiveTab(foundItem ? foundItem.key : "");
    }
  }, [location.pathname]);

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setDropdownOpen(false);
  };

  const handleNavigation = (direction) => {
    if (direction === "next") {
      window.history.forward();
    } else {
      window.history.back();
    }
  };

  return (
    <nav className="md:pr-12 md:pl-14 py-1 font-inter font-bold w-full mx-auto bg-transparent z-50 relative flex items-center justify-between md:justify-start md:space-x-2">
      
      {/* Logo & Chevron */}
      <div className="flex items-center space-x-2">
        {location.pathname !== "/newsmain" && (
          <div
            className={`relative transition-transform md:mr-0 md:pl-0 pl-3 duration-500 ${
              flipped ? "rotate-[180deg]" : "rotate-0"
            }`}
            onMouseEnter={() => setFlipped(true)}
            onMouseLeave={() => setFlipped(false)}
            ref={chevronRef}
          >
            <img
              src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745780563/Expand_left_eojpqt.png"
              alt="Chevron"
              className="cursor-pointer transition-transform hover:scale-110 xl:w-[35px] xl:h-[35px] md:w-[40px] md:h-[40px] w-[20px] h-[20px] object-contain"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const { left, width } = e.target.getBoundingClientRect();
                const clickPosition = e.clientX - left;
                if (clickPosition < width / 4) {
                  handleNavigation("back");
                } else {
                  handleNavigation("next");
                }
              }}
            />
          </div>
        )}

        {/* Logo */}
        <a href="/">
          <img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745779623/image_1705_1_evjfnk.png"
            alt="Logo"
            className="xl:w-[190px] xl:h-[150px] md:mr-0 md:w-[150px] md:h-[106px] w-[120px] h-[70px] object-contain md:px-0 px-4"
          />
        </a>
      </div>

      {/* Desktop Menu */}
      <div className="md:flex hidden space-x-1">
        <ul className="flex space-x-2 items-center">
          {menuItems.map((item) => (
            <li
              key={item.key}
              className={`
                md:rounded-[5px] transition-all duration-300 text-center shadow-gray-400 shadow-md
                ${activeTab === item.key
                  ? "bg-[#03897B] text-white"
                  : "bg-[#F3F5F5] text-black hover:bg-gray-300"}
                ${
                  ["home", "reports", "services", "gallery"].includes(item.key)
                    ? "md:w-[80px] xl:w-[185px] lg:w-[150px]"
                    : "md:w-[120px] xl:w-[168px] lg:w-[190px]"
                }
                menu-box
              `}
            >
              <Link
                to={item.path}
                onClick={() => setActiveTab(item.key)}
                className="block text-[10px] lg:text-[19px] font-bold tracking-wide xl:text-[24px] lg:h-[50px] xl:h-[60px] lg:py-3 xl:py-3 w-full"
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Mobile Menu Toggle & Country Dropdown */}
      <div className="flex">
        <div
          className="md:hidden flex justify-between items-center gap-2 mr-4 cursor-pointer z-50"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? (
            <svg
              className="w-6 h-6 text-black"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <img
              src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745783267/Menu_pwna25.png"
              alt="menu"
              className="w-[24px] h-[24px]"
            />
          )}
        </div>

        <div className="relative z-50">
          <button
            className="flex items-center space-x-0 text-sm text-gray-700 p-1 rounded-lg"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <img
              src={selectedCountry.flag}
              alt={selectedCountry.name}
              className="md:w-[78px] md:h-14 w-5 h-4"
            />
            <img
              src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745780963/Expand_down_12_e921km.png"
              alt="expand"
              className="md:w-[30px] md:h-[30px] h-[12px] w-[12px]"
            />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-12 bg-[#03897B] shadow-lg rounded-lg mt-1 w-48 z-50">
              <ul className="space-y-2 py-2">
                {countries.map((country) => (
                  <li
                    key={country.code}
                    className="flex items-center space-x-2 px-4 py-2 cursor-pointer hover:text-white hover:bg-[#20342d]"
                    onClick={() => handleCountryChange(country)}
                  >
                    <img src={country.flag} alt={country.name} className="w-4 h-4" />
                    <span className="text-white hover:text-[#D2F34A] text-[18px]">
                      {country.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Nav Menu with Transition */}
      <div
        className={`md:hidden fixed top-0 left-0 w-full h-screen bg-black/60 z-40 transition-opacity duration-300 ${
          menuOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        <ul
          className={`absolute top-16 left-0 w-full bg-white/80 shadow-md flex flex-col items-start space-y-2 py-4 px-4 transform transition-transform duration-300 ${
            menuOpen ? "translate-y-0" : "-translate-y-10"
          }`}
        >
          {menuItems.map((item) => (
            <Link
              key={item.key}
              to={item.path}
              onClick={() => {
                setActiveTab(item.key);
                setMenuOpen(false);
              }}
              className={`w-full block rounded py-3 px-2 cursor-pointer
                transition-all duration-300
                ${activeTab === item.key
                  ? "bg-[#03897B] text-white"
                  : "bg-white/60 text-black hover:bg-gray-200"}
              `}
            >
              {item.label}
            </Link>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
